<script lang="ts">
  import SectionTitle from '$components/section-title.svelte';
  import { storyblokEditable } from '$lib/actions/storyblok-editable';
  import type { ProductsShowcaseStoryblok } from '$types/bloks';
  import { isCmsStory } from '$lib/utils/cms';
  import { getAnchorFromCmsLink } from '$lib/utils/cms';
  import ProductGrid from '$components/product-grid.svelte';
  import { createQueries } from '@tanstack/svelte-query';
  import { shopify } from '$lib/shopify';
  import { lang } from '$lib/lang';
  import type { LanguageCode } from '$gql/storefront/schemas';
  import { optimalUSCountry, parseCountryAsCode } from '$lib/country';

  export let block: ProductsShowcaseStoryblok;

  const cta = block.cta ? block?.cta[0] : null;

  // The goal is to delete block.product completely to avoid doing resolve relations
  // of product pages. However, we can't delete it yet since it's being used in so many places.
  // So we'll support both properties for now.
  const handlesByStories =
    block.products?.reduce<string[]>((acc, product) => {
      if (isCmsStory(product)) {
        acc.push(product.slug);
      }
      return acc;
    }, []) || [];

  const handlesByLinks =
    block.products_links?.reduce<string[]>((acc, product) => {
      if (product.link) {
        const link = $getAnchorFromCmsLink(product.link);
        const handle = link?.href?.split('/').pop();

        acc.push(handle || '');
      }

      return acc;
    }, []) || [];

  const handles = handlesByLinks.length > 0 ? handlesByLinks : handlesByStories;

  const queries = createQueries(
    handles.map((handle) => ({
      staleTime: 5 * 60 * 1000,
      queryKey: ['productCard', handle],
      queryFn: async () => {
        return await $shopify.getProductCard({
          handle,
          lang: $lang.toUpperCase() as LanguageCode,
          country: parseCountryAsCode($optimalUSCountry)
        });
      }
    }))
  );

  $: arr = $queries.map((q) => q.data?.product);
  $: products = arr.filter(Boolean) as NonNullable<(typeof arr)[number]>[];
</script>

<section class="overflow-hidden px-2 pt-16 pb-2 lg:pt-[72px] lg:pb-8 lg:px-8">
  <div class="container isolate mx-auto" use:storyblokEditable={block}>
    {#if block.title?.[0]}
      <SectionTitle block={block.title[0]} />
    {/if}
    <ProductGrid
      variant="grid"
      {products}
      loading={$queries.some((q) => q.isLoading)}
      highlightPosition={block.variant}
      expectedResults={handles.length}
      removeMobileHighlight={block.remove_highlight_on_mobile}
      {cta}
    />
  </div>
</section>
